import Icon from "@/icons";
import { useNavigate, useParams } from "react-router-dom";
import type { Channel } from "types";
import { UpdateChannel } from "../Dialogs/UpdateChannel";
import { useVoice } from "@/VoiceSystem";
import { Avatar, AvatarFallback, AvatarImage } from "@radix-ui/react-avatar";
import { UserPopover } from "../Popover/User";
import Tippy from "@tippyjs/react";
import { useEffect, useState } from "react";
import * as Chime from 'amazon-chime-sdk-js';
import { activeSpeakerEmitter } from "@/eventEmitter";
import { useQueryClient } from "@tanstack/react-query";

export default function Channel({ channel, hasPermission }: { channel: Channel, hasPermission: BigInt }) {
    const { teamId, channelId } = useParams();
    const isActive = channelId === channel.id;
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const members = queryClient.getQueryData(["team", teamId, "members"]) as any;
    const unreadedMessages = queryClient.getQueryData(["unread"]) as any;
    const member = queryClient.getQueryState(["team", teamId, "members", "@me"]) as any;
    let voice = useVoice() as any;
    const redirect = (channel: Channel) => {
        if (channel.type === "voice") {
            voice.joinVoiceChannel(channel, teamId);
        } else
            navigate(`/teams/${teamId}/channels/${channel.id}`);
    };
    const [currentSpeakers, setCurrentSpeakers] = useState<string[]>([]);

    const subscribeToActiveSpeaker = () => {
        if (!voice.meetingSessionRef.current?.audioVideo) return;
        if (channel.id !== voice.channel?.id) return;

        const activeSpeakerPolicy = new Chime.DefaultActiveSpeakerPolicy(0.05);
        voice.meetingSessionRef.current.audioVideo.subscribeToActiveSpeakerDetector(
            activeSpeakerPolicy,
            (attendeeIds: string[]) => {
                activeSpeakerEmitter.emit('updateSpeakers', attendeeIds);
            }
        );
    };

    useEffect(() => {
        subscribeToActiveSpeaker();
    }, [voice.meetingSessionRef.current?.audioVideo]);

    activeSpeakerEmitter.on('updateSpeakers', (attendeeIds: string[]) => {
        setCurrentSpeakers(attendeeIds);
    });

    return (
        <>
            <div className="flex items-center justify-center">
                <div className="justify-center items-center w-full flex">
                    {unreadedMessages?.teamInfo?.find((teamc: any) => teamc.teamId == teamId)?.channelInfo?.[channel.id] && (
                       <div className="absolute flex items-center justify-center left-0 w-2 h-4 bg-white rounded-r-xl"/>
                    )}
                    <div className="px-2 w-full flex-col flex items-start">
                        <div
                            onClick={() => redirect(channel)}
                            key={channel.id}
                            className={`group justify-between min-h-10 min-w-12 w-full flex items-center group gap-2 px-2 rounded-full hover:bg-brand-darkest cursor-pointer ${isActive ? "bg-brand-darkest" : ""} ${channel?.participants?.find((participant) => participant.id === member?.data?.member?.id) ? "bg-brand-back/25" : ""}`}
                            data-channel-id={channel.id}
                        >

                            <div className="flex items-center gap-2 group-hover:text-white text-gray-400">
                                {
                                    channel.type === "text" ?
                                        <Icon name="hashtag" size="16" />
                                        : channel.type === "todo" ?
                                            <Icon name="todo" size="16" />
                                            : channel.type === "watchstream" ?
                                                <Icon name="stream" size="16" />
                                                : channel.type === "whiteboard" ?
                                                    <Icon name="whiteboard" size="16" />
                                                    : channel.type === "voice" ?
                                                        <Icon name="volume" size="16" />
                                                        :
                                                        <Icon name="hashtag" size="16" />
                                }
                                <h1 className={`text-${unreadedMessages?.teamInfo?.find((teamc: any) => teamc.teamId == teamId)?.channelInfo?.[channel.id] ? "white" : "gray-300"
                                    } font-semibold text-base group-hover:text-white truncate`}>{channel.name}</h1>
                                {channel.type == "voice" && (
                                    <div className="p-1">
                                        <Tippy content="Voice Channels are in beta" placement="top">
                                            <div className="bg-brand-green px-1 flex items-center rounded-lg">
                                                <h1 className="text-white font-semibold text-xs">BETA</h1>
                                            </div>
                                        </Tippy>
                                    </div>
                                )}

                                {channel.participants?.find((participant) => participant.isStreaming) && (
                                    <div className="p-1">
                                        <Tippy content="Someone is streaming" placement="top">
                                            <div className="bg-gradient-to-r px-2 from-purple-500 to-purple-800 flex items-center rounded-lg">
                                                <h1 className="text-white font-semibold text-xs">STREAM</h1>
                                            </div>
                                        </Tippy>
                                    </div>
                                )}
                            </div>
                            <div className="flex items-center gap-2">

                                {channel.additionalData?.streamPlatform == "twitch" && (
                                    <Icon name="twitch" size="16" color="#a3a3ac" />
                                )}

                                {hasPermission && (
                                    <div className="hidden group-hover:flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                                    <UpdateChannel channel={channel} >
                                        <div className="hidden cursor-pointer group-hover:flex items-center justify-center px-2 h-8 w-8 rounded-full transition-all duration-300">
                                            <Icon name="settings" size="60" color="gray" />
                                        </div>
                                    </UpdateChannel>
                                    </div>
                                )}

                                {unreadedMessages?.teamInfo?.find((teamc: any) => teamc.teamId == teamId)?.badgingInfo?.[channel.id] && (
                                    <div className="flex items-center justify-center px-2 h-4 w-4 rounded-full bg-red-500">
                                        <h1 className="text-white font-semibold text-xs">{unreadedMessages?.teamInfo?.find((teamc: any) => teamc.teamId == teamId)?.badgingInfo?.[channel.id].unread > 9 ? "9+" : unreadedMessages?.teamInfo?.find((teamc: any) => teamc.teamId == teamId)?.badgingInfo?.[channel.id].unread}</h1>
                                    </div>
                                )}

                                {channel.type === "text" && unreadedMessages?.teamInfo?.find((teamc: any) => teamc.teamId == teamId)?.channelInfo?.[channel.id] && (
                                    <div className="flex items-center justify-center px-2  rounded-full">
                                        <h1 className="text-brand-green font-extrabold text-xs">{unreadedMessages?.teamInfo?.find((teamc: any) => teamc.teamId == teamId)?.channelInfo?.[channel.id].unread > 999 ? "999+" : unreadedMessages?.teamInfo?.find((teamc: any) => teamc.teamId == teamId)?.channelInfo?.[channel.id].unread} New</h1>
                                    </div>
                                )}

                            </div>
                        </div>


                        {channel.type === "voice" && channel.participants.length > 0 && (
                            <div className="flex flex-col gap-2 p-3 flex-grow w-full">
                                {channel.participants.map((participant) => (
                                    <>
                                        {members?.members ? (
                                            <UserPopover user={members?.members ? members?.members.find((member: any) => member.id === participant.id) : ""} key={participant.id + "-" + channel.id}>
                                                <div key={participant.id} className="flex justify-between items-center gap-2 p-1 w-full rounded-lg hover:bg-brand-darkest cursor-pointer">
                                                    <div className={`items-center flex gap-2 w-full ${participant.isConnecting ? "brightness-75" : ""}`}>
                                                        <div className="w-full flex items-center gap-2 p-2 rounded-lg cursor-pointer">
                                                            <Avatar className={`w-6 h-6 rounded-full bg-gray-600 items-center flex justify-center ${voice?.channel?.id == channel.id && currentSpeakers.includes(channel.additionalData?.attendeeById?.[participant.id] as string) ? "ring-2 ring-green-500" : ""}`}>
                                                                <AvatarImage className="rounded-full" src={members?.members ? members?.members.find((member: any) => member.id === participant.id)?.profilePicture : ""} />
                                                                <AvatarFallback className="text-sm">{members?.members ? members?.members.find((member: any) => member.id === participant.id)?.username[0] : ""} </AvatarFallback>
                                                            </Avatar>
                                                            <div className={`flex items-start rounded-lg hover:bg-brand-darkest cursor-pointer ${voice?.channel?.id == channel.id && currentSpeakers.includes(channel.additionalData?.attendeeById?.[participant.id] as string) ? "text-white" : "text-gray-400"}`}>
                                                                <div className="rounded-lg font-semibold text-lg">{members?.members ? members?.members.find((member: any) => member.id === participant.id)?.username : ""}</div>
                                                            </div>
                                                        </div>

                                                        <div className="mr-2 flex gap-2">
                                                            {participant.isMuted && (
                                                                <Tippy content="Muted" placement="top">
                                                                    <div className="text-gray-300/50 font-bold">
                                                                        <Icon name="voice-muted" size="12" />
                                                                    </div>
                                                                </Tippy>
                                                            )}

                                                            {participant.isDeafened && (
                                                                <Tippy content="Deafened" placement="top">
                                                                    <div className="text-gray-300/50 font-bold">
                                                                        <Icon name="voice-deafened" size="12" />
                                                                    </div>
                                                                </Tippy>
                                                            )}

                                                            {participant.isStreaming && (
                                                                <Tippy content="Streaming" placement="top">
                                                                    <div className="text-purple-500 font-bold">
                                                                        <Icon name="icon-share-screen" size="12" />
                                                                    </div>
                                                                </Tippy>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </UserPopover>
                                        ) : null}
                                    </>
                                ))}

                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}
